import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { managerCheckIn } from '../../../services/timeTrackingService';

// Reason codes for manager check-in
const CHECKIN_REASONS = [
  { code: 'forgot_phone', label: 'Technician forgot phone' },
  { code: 'app_issues', label: 'App not working' },
  { code: 'new_employee', label: 'New employee training' },
  { code: 'other', label: 'Other (see note)' }
];

/**
 * ManagerCheckInModal - Modal for managers to check in a technician
 *
 * Props:
 * - show: Boolean - whether modal is visible
 * - onHide: Function - called when modal is closed
 * - onSuccess: Function - called after successful check-in
 * - technician: Object - { User_id, user_name } of the technician
 * - locationId: Number - the location ID to check in at
 * - actorUserId: Number - the manager's user ID performing the action
 */
class ManagerCheckInModal extends Component {
  state = {
    reasonCode: '',
    note: '',
    loading: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    // Reset state when modal opens
    if (this.props.show && !prevProps.show) {
      this.setState({
        reasonCode: '',
        note: '',
        loading: false,
        error: null
      });
    }
  }

  handleReasonChange = (e) => {
    this.setState({ reasonCode: e.target.value, error: null });
  };

  handleNoteChange = (e) => {
    this.setState({ note: e.target.value });
  };

  handleSubmit = async () => {
    const { technician, locationId, onSuccess, onHide, actorUserId } = this.props;
    const { reasonCode, note } = this.state;

    // Validate reason
    if (!reasonCode) {
      this.setState({ error: 'Please select a reason for checking in this technician.' });
      return;
    }

    // Validate note for "other" reason
    if (reasonCode === 'other' && !note.trim()) {
      this.setState({ error: 'Please provide a note explaining the reason.' });
      return;
    }

    try {
      this.setState({ loading: true, error: null });

      await managerCheckIn(technician.User_id, locationId, reasonCode, note, actorUserId);

      // Success - close modal and notify parent
      onHide();
      if (onSuccess) {
        onSuccess(technician);
      }
    } catch (err) {
      console.error('Manager check-in error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to check in technician. Please try again.';
      this.setState({ error: errorMsg, loading: false });
    }
  };

  render() {
    const { show, onHide, technician } = this.props;
    const { reasonCode, note, loading, error } = this.state;

    const technicianName = technician?.user_name || 'Technician';

    return (
      <Modal show={show} onHide={onHide} centered backdrop={loading ? 'static' : true}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-sign-in text-success mr-2"></i>
            Check In {technicianName}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Reason Selection */}
          <div className="form-group">
            <label className="font-weight-bold">
              Reason for Manual Check-In <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={reasonCode}
              onChange={this.handleReasonChange}
              disabled={loading}
            >
              <option value="">Select a reason...</option>
              {CHECKIN_REASONS.map(reason => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
            </select>
            <small className="form-text text-muted">
              This will be recorded in the time tracking audit log.
            </small>
          </div>

          {/* Note Field */}
          <div className="form-group">
            <label className="font-weight-bold">
              Note {reasonCode === 'other' && <span className="text-danger">*</span>}
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={note}
              onChange={this.handleNoteChange}
              placeholder="Add any additional details..."
              disabled={loading}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger mb-0">
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={this.handleSubmit}
            disabled={loading || !reasonCode}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                Checking In...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-sign-in mr-2"></i>
                Check In
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ManagerCheckInModal;
